import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { RiUploadCloud2Line, RiCloseLine } from "react-icons/ri";
// import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const UPLOAD_PRESET = process.env.REACT_APP_UPLOAD_PRESET;
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_NAME;

const fieldConfig = [
  { key: "name", label: "Full Name", type: "text", icon: "👤" },
  { key: "email", label: "Email Address", type: "email", icon: "✉️" },
  { key: "number", label: "Phone Number", type: "tel", icon: "📞" },
  { key: "position", label: "Position", type: "text", icon: "💼" },
  { key: "address", label: "Address", type: "text", icon: "📍" },
  {
    key: "userType",
    label: "User Type",
    type: "text",
    icon: "🔑",
    readOnly: true,
  },
];

function getInitials(name = "") {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// Profile Picture Upload Component
const ProfilePicUpload = ({ value, onChange, disabled }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", UPLOAD_PRESET);
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        formData,
      );
      onChange(res.data.secure_url);
    } catch {
      // silently fail
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Avatar */}
      <div className="relative group">
        <div className="w-20 h-20 rounded-2xl bg-white border-4 border-white shadow-lg overflow-hidden flex items-center justify-center text-blue-600 text-2xl font-bold select-none">
          {value ? (
            <img
              src={value}
              alt="Profile"
              className="w-full h-full object-contain"
            />
          ) : (
            <span>{getInitials("")}</span>
          )}
        </div>
        {/* Overlay on hover when editing */}
        {!disabled && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="absolute inset-0 rounded-2xl bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity disabled:cursor-not-allowed"
          >
            {uploading ? (
              <AiOutlineLoading3Quarters className="w-5 h-5 text-white animate-spin" />
            ) : (
              <RiUploadCloud2Line className="w-5 h-5 text-white" />
            )}
          </button>
        )}
      </div>

      {/* Upload button (visible only in edit mode) */}
      {!disabled && (
        <div className="flex flex-col items-center gap-1">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <AiOutlineLoading3Quarters className="w-3 h-3 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <RiUploadCloud2Line className="w-3 h-3" />
                {value ? "Change Photo" : "Upload Photo"}
              </>
            )}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 transition"
            >
              <RiCloseLine className="w-3 h-3" /> Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default function AdminProfile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passForm, setPassForm] = useState({
    oldPassword: "",
    newPassword: "",
  });
  const [changingPass, setChangingPass] = useState(false);

  const storedData = JSON.parse(localStorage.getItem("Nexgen-Admin-Data"));
  const id = storedData?._id;

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const storedData = JSON.parse(localStorage.getItem("Nexgen-Admin-Data"));
      const id = storedData?._id;

      if (!id) {
        setError("No admin ID found in localStorage.");
        setLoading(false);
        return;
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/user/${id}`,
      );

      if (!res.ok) throw new Error(`Server returned ${res.status}`);

      const data = await res.json();
      setUser(data);
      setFormData(data);
    } catch (err) {
      setError(err.message || "Failed to fetch user.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setChangingPass(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/change-password/${user._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(passForm),
        },
      );

      const result = await res.json();

      if (!res.ok) throw new Error(result.message);

      showToast("success", "Password changed successfully!");
      setShowPasswordModal(false);
      setPassForm({ oldPassword: "", newPassword: "" });
    } catch (err) {
      showToast("error", err.message);
    } finally {
      setChangingPass(false);
    }
  };

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/user/${user._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        },
      );

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Update failed");

      const updatedUser = result.data || result;
      setUser(updatedUser);
      setFormData(updatedUser);
      localStorage.setItem("Nexgen-Admin-Data", JSON.stringify(updatedUser));
      setIsEditing(false);
      showToast("success", "Profile updated successfully!");
    } catch (err) {
      showToast("error", err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(user);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="w-12 h-12 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin" />
        <p className="mt-4 text-sm text-slate-400 tracking-wide">
          Loading profile…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-10 text-center max-w-sm w-full">
          <div className="text-5xl mb-4">⚠️</div>
          <p className="text-red-600 text-sm whitespace-pre-line">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-10 px-4">
      {toast && (
        <div
          className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-xl text-sm font-medium shadow-md border transition-all duration-300
            ${
              toast.type === "success"
                ? "bg-green-50 text-green-700 border-green-200"
                : "bg-red-50 text-red-600 border-red-200"
            }`}
        >
          {toast.type === "success" ? "✅" : "❌"} {toast.text}
        </div>
      )}

      {showPasswordModal &&
        createPortal(
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] px-4">
            <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl p-6 border border-slate-200 animate-fadeIn">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  🔐 Change Password
                </h3>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="text-slate-400 hover:text-red-500 transition"
                >
                  <RiCloseLine size={20} />
                </button>
              </div>

              {/* Inputs */}
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500">
                    Old Password
                  </label>
                  <input
                    type="password"
                    value={passForm.oldPassword}
                    onChange={(e) =>
                      setPassForm((prev) => ({
                        ...prev,
                        oldPassword: e.target.value,
                      }))
                    }
                    className="w-full mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passForm.newPassword}
                    onChange={(e) =>
                      setPassForm((prev) => ({
                        ...prev,
                        newPassword: e.target.value,
                      }))
                    }
                    className="w-full mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 rounded-lg bg-slate-100 text-slate-600 text-sm hover:bg-slate-200"
                >
                  Cancel
                </button>

                <button
                  onClick={handleChangePassword}
                  disabled={changingPass}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm flex items-center gap-2 disabled:opacity-60"
                >
                  {changingPass && (
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <div className="w-full mx-auto space-y-4">
        {/* Header Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="h-28 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600" />

          <div className="px-6 pb-6">
            <div className="flex items-end justify-between -mt-10 mb-4">
              {/* Profile Picture */}
              {isEditing ? (
                <ProfilePicUpload
                  value={formData.profilePic || ""}
                  onChange={(url) =>
                    setFormData((prev) => ({ ...prev, profilePic: url }))
                  }
                  disabled={false}
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-white border-4 border-white shadow-lg overflow-hidden flex items-center justify-center text-blue-600 text-2xl font-bold select-none">
                  {user?.profilePic ? (
                    <img
                      src={user.profilePic}
                      alt={user.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    getInitials(user?.name)
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-2 mt-2">
                {!isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-colors"
                    >
                      ✏️ Edit Profile
                    </button>

                    {/* CHANGE PASSWORD BUTTON */}
                    <button
                      onClick={() => setShowPasswordModal(true)}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-xl transition-colors"
                    >
                      🔐 Change Password
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 text-sm font-medium text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-60 rounded-xl transition-colors"
                    >
                      {saving ? (
                        <>
                          <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Saving…
                        </>
                      ) : (
                        "✓ Save Changes"
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>

            <h1 className="text-xl font-semibold text-slate-800 leading-tight">
              {user?.name}
            </h1>
            <div className="flex items-center flex-wrap gap-2 mt-1.5">
              <span className="text-sm text-slate-500">{user?.position}</span>
              <span className="text-slate-300">·</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-100 capitalize">
                {user?.userType}
              </span>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-5">
            Profile Information
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {fieldConfig.map(({ key, label, type, icon, readOnly }) => (
              <div
                key={key}
                className={key === "address" ? "sm:col-span-2" : ""}
              >
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                  <span className="mr-1">{icon}</span>
                  {label}
                </label>

                {isEditing && !readOnly ? (
                  <input
                    type={type}
                    value={formData[key] || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2.5 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition placeholder-slate-300"
                  />
                ) : (
                  <div className="px-4 py-2.5 text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-xl">
                    {user[key] || <span className="text-slate-300">—</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
